import { Meta, theme } from 'aimo-ui';
import { ParkZoneWithVisibility } from 'components/park/ParkingZoneSearch';
import FlatListCustom from 'components/sheets/FlatListCustom';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useMapStore } from 'stores/mapStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { useSearchStore } from 'stores/searchStore';
import { Divider } from 'styles/CommonLayoutStyles';
import { Maybe, ParkZone } from 'types/generatedSchemaTypes';
import { makePoint } from 'utils/mapUtils';
import { distanceCompare, substringSearch } from 'utils/parkUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import NoMatches from './NoMatches';
import SearchItem from './SearchItem';
import { Container, MarginView, Row } from './styles/styles';

type ParkingZoneFilterProps = {
  searchTerm: string;
  testID?: string;
};

const ItemSeparator = () => <Divider marginVertical={10} />;
const ListFooter = () => <MarginView />;

const ParkingZoneFilterList = ({
  searchTerm,
  testID,
}: ParkingZoneFilterProps) => {
  const { t } = useTranslation();
  const currentLocation = useMapStore((state) => state.userLocation);
  const { filterParkzoneData } = useParkzoneStore();
  const { resetSearch, setSearchInputActive } = useSearchStore(
    (state) => state
  );
  const searchResults: Maybe<ParkZone>[] = useMemo(() => {
    const zones = searchTerm
      ? substringSearch(searchTerm, filterParkzoneData || [], [
          'name',
          'commercialZoneView.zoneCode',
          'address.municipality',
          'address.city',
        ])
      : filterParkzoneData || [];
    return zones.sort((a, b) => distanceCompare(a, b, currentLocation));
  }, [searchTerm, filterParkzoneData, currentLocation]);

  useEffect(() => {
    if (searchResults?.length > 0) {
      const firstCoordinates = searchResults?.[0]?.address?.marker?.coordinates;
      if (firstCoordinates) {
        useMapStore.getState().setMapCoordinates(makePoint(firstCoordinates));
      }
    }
  }, [searchResults]);

  if (
    (searchTerm.length > 3 ||
      (!Number.isNaN(Number(searchTerm)) && searchTerm.length >= 2)) &&
    searchResults?.length === 0
  ) {
    return <NoMatches onButtonClick={() => setSearchInputActive(false)} />;
  }

  const handleClearFilter = () => {
    useMapStore.getState().setMapFilters({
      parkingAvailable: false,
      chargingAvailable: false,
      permits: false,
      prepaidsAvailable: false,
    });
    resetSearch();
  };

  return (
    <Container testID={testID}>
      {!!searchResults && (
        <>
          <Row mt={15} mb={15}>
            <Meta
              {...testIdentifiers('results-label')}
              testID="results-label"
              color={theme.colors.gray300}>
              {t('search.results', { amount: searchResults.length })}
            </Meta>
            <View
              style={{
                marginHorizontal: 8,
                width: 5,
                height: 5,
                backgroundColor: theme.colors.gray100,
                borderRadius: 2.5,
              }}
            />
            <Meta
              {...testIdentifiers('clear-filter')}
              testID="clear-filter"
              color={theme.colors.primary}
              accessibilityRole="button"
              onPress={handleClearFilter}>
              {t('search.zoneFilter.clearFilter')}
            </Meta>
          </Row>
          <FlatListCustom
            ItemSeparatorComponent={ItemSeparator}
            data={searchResults as ParkZoneWithVisibility[]}
            removeClippedSubviews={false}
            renderItem={({ item }) => (
              <SearchItem item={item} currentLocation={currentLocation} />
            )}
            ListFooterComponent={ListFooter}
            keyExtractor={(item, index) => `${item?.uid}-${index}`}
            {...testIdentifiers('search-results-list')}
          />
        </>
      )}
    </Container>
  );
};

export default ParkingZoneFilterList;
